// admin/src/components/QuestionEditModal.jsx
import React, { useState, useEffect } from 'react';

const API_ROOT = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const PHASE_OPTIONS = [
  { value: 1, label: '🧠 Phase 1: Mindset', description: 'Questions that identify personality & learning preferences' },
  { value: 2, label: '🎯 Phase 2: Bucket Discovery', description: 'Questions that map interests to broad career buckets' },
  { value: 3, label: '🔍 Phase 3: Area Refinement', description: 'Questions that narrow down to specific areas (targeted by bucket)' }
];

const BUCKET_OPTIONS = [
  { value: 'build_make', label: '🔨 Build & Make' },
  { value: 'understand_discover', label: '🔬 Understand & Discover' },
  { value: 'express_create', label: '🎨 Express & Create' },
  { value: 'help_connect', label: '❤️ Help & Connect' },
  { value: 'lead_organize', label: '📊 Lead & Organize' },
  { value: 'explore_experience', label: '🌍 Explore & Experience' },
  { value: 'protect_sustain', label: '🛡️ Protect & Sustain' },
  { value: 'think_compute', label: '💻 Think & Compute' }
];

/**
 * QuestionEditModal - Full-featured modal for creating/editing questions
 * 
 * Props:
 * - question: existing question object (or null for new)
 * - isOpen: boolean
 * - onClose: function
 * - onSave: async function(questionData) => saved question
 * - tenantId: string (from context)
 * - adminSecret: string (from context)
 */
export default function QuestionEditModal({ question, isOpen, onClose, onSave, tenantId, adminSecret }) {
  const isNew = !question?._id;
  
  const [formData, setFormData] = useState({
    text: '',
    type: 'single',
    category: 'interest',
    phase: 1,
    bucket: '',
    order: 0,
    enabled: true,
    options: []
  });
  
  const [availableMindsetTags, setAvailableMindsetTags] = useState([]);
  const [availableAreaTags, setAvailableAreaTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Fetch tags when modal opens
  useEffect(() => {
    if (!isOpen) return;
    
    const fetchTags = async () => {
      setLoadingTags(true);
      try {
        // Fetch mindset tags
        const mindsetRes = await fetch(
          `${API_ROOT}/api/tags?tenantId=${encodeURIComponent(tenantId)}&category=mindset`,
          {
            headers: {
              'x-admin-secret': adminSecret || ''
            }
          }
        );
        if (mindsetRes.ok) {
          const mindsetTags = await mindsetRes.json();
          setAvailableMindsetTags(mindsetTags);
        }

        // Fetch area tags
        const areaRes = await fetch(
          `${API_ROOT}/api/tags?tenantId=${encodeURIComponent(tenantId)}&category=area`,
          {
            headers: {
              'x-admin-secret': adminSecret || ''
            }
          }
        );
        if (areaRes.ok) {
          const areaTags = await areaRes.json();
          setAvailableAreaTags(areaTags);
        }
      } catch (err) {
        console.error('Error loading tags:', err);
      } finally {
        setLoadingTags(false);
      }
    };

    fetchTags();
  }, [isOpen, tenantId, adminSecret]);

  // Reset form when question changes or modal opens
  useEffect(() => {
    if (isOpen && question) {
      setFormData({
        text: question.text || '',
        type: question.type || 'single',
        category: question.category || 'interest',
        phase: question.phase || 1,
        bucket: question.bucket || '',
        order: question.order || 0,
        enabled: question.enabled !== false,
        options: Array.isArray(question.options) 
          ? question.options.map(opt => ({
              text: opt.text || '',
              tags: Array.isArray(opt.tags) ? [...opt.tags] : [],
              areaOfStudyTags: Array.isArray(opt.areaOfStudyTags) ? [...opt.areaOfStudyTags] : [],
              mindsetTag: opt.mindsetTag || '',
              bucket: opt.bucket || '',
              weight: opt.weight || 1
            }))
          : []
      });
    } else if (isOpen && !question) {
      setFormData({
        text: '',
        type: 'single',
        category: 'interest',
        phase: 1,
        bucket: '',
        order: 0,
        enabled: true,
        options: []
      });
    }
    setError('');
    setSaving(false);
  }, [isOpen, question]);

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-set category based on phase
      if (field === 'phase') {
        if (value === 1) {
          updated.category = 'mindset';
        } else if (value === 2 || value === 3) {
          updated.category = 'interest';
        }
        
        // Clear bucket if not phase 3
        if (value !== 3) {
          updated.bucket = '';
        }
      }
      
      return updated;
    });
  };

  const addOption = () => {
    setFormData(prev => ({
      ...prev,
      options: [
        ...prev.options,
        {
          text: '',
          tags: [],
          areaOfStudyTags: [],
          mindsetTag: '',
          bucket: '',
          weight: 1
        }
      ]
    }));
  };

  const removeOption = (index) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const updateOption = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => 
        i === index ? { ...opt, [field]: value } : opt
      )
    }));
  };

  const toggleOptionTag = (optionIndex, tagSlug, tagType) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => {
        if (i !== optionIndex) return opt;
        
        const field = tagType === 'area' ? 'areaOfStudyTags' : 'tags';
        const currentTags = opt[field] || [];
        
        return {
          ...opt,
          [field]: currentTags.includes(tagSlug)
            ? currentTags.filter(t => t !== tagSlug)
            : [...currentTags, tagSlug]
        };
      })
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.text.trim()) {
      setError('Question text is required');
      return;
    }
    
    if (formData.options.length === 0) {
      setError('At least one option is required');
      return;
    }

    if (formData.phase === 3 && !formData.bucket) {
      setError('Phase 3 questions must have a bucket assigned');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        tenantId,
        text: formData.text.trim(),
        // Clean up empty tags from options
        options: formData.options.map(opt => ({
          text: opt.text.trim(),
          tags: (opt.tags || []).filter(t => t),
          areaOfStudyTags: (opt.areaOfStudyTags || []).filter(t => t),
          mindsetTag: opt.mindsetTag || undefined,
          bucket: opt.bucket || undefined,
          weight: opt.weight || 1
        }))
      };
      
      if (question?._id) {
        payload._id = question._id;
      }

      await onSave(payload);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save question');
      setSaving(false);
    }
  };

  if (!isOpen) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="modal-content" 
        onClick={(e) => e.stopPropagation()} 
        style={{ maxWidth: 900, maxHeight: '90vh', overflowY: 'auto' }}
      >
        <div className="modal-header">
          <h2>{isNew ? '➕ New Question' : '✏️ Edit Question'}</h2>
          <button 
            className="modal-close" 
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error" role="alert" style={{ marginBottom: 16 }}>
              {error}
            </div>
          )}

          {/* Question Text */}
          <div className="form-group">
            <label htmlFor="text">
              Question Text <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <textarea
              id="text"
              className="input"
              value={formData.text}
              onChange={(e) => handleChange('text', e.target.value)}
              placeholder="e.g., What activities do you enjoy most?"
              rows={3}
              required
              autoFocus
            />
          </div>

          {/* Phase Selection */}
          <div className="form-group">
            <label htmlFor="phase">
              Phase <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <select
              id="phase"
              className="input"
              value={formData.phase}
              onChange={(e) => handleChange('phase', Number(e.target.value))}
            >
              {PHASE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 4 }}>
              {PHASE_OPTIONS.find(p => p.value === formData.phase)?.description}
            </div>
          </div>

          {/* Bucket (only for Phase 3) */}
          {formData.phase === 3 && (
            <div className="form-group">
              <label htmlFor="bucket">
                Target Bucket <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <select
                id="bucket"
                className="input"
                value={formData.bucket}
                onChange={(e) => handleChange('bucket', e.target.value)}
              >
                <option value="">Select a bucket...</option>
                {BUCKET_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 4 }}>
                This question will only show to students whose Phase 2 answers indicated interest in this bucket
              </div>
            </div>
          )}

          {/* Type & Order */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label htmlFor="type">Answer Type</label>
              <select
                id="type"
                className="input"
                value={formData.type}
                onChange={(e) => handleChange('type', e.target.value)}
              >
                <option value="single">Single Select</option>
                <option value="multiple">Multiple Select</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="order">Display Order</label>
              <input
                id="order"
                type="number"
                className="input"
                value={formData.order}
                onChange={(e) => handleChange('order', Number(e.target.value))}
                min="0"
              />
            </div>
          </div>

          {/* Category (auto-set based on phase, shown for reference) */}
          <div className="form-group">
            <label>Category (auto-set by phase)</label>
            <input
              type="text"
              className="input"
              value={formData.category}
              disabled
              style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
            />
          </div>

          {/* Options */}
          <div className="form-group">
            <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>
                Answer Options <span style={{ color: '#ef4444' }}>*</span>
              </span>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={addOption}
                style={{ fontSize: '0.875rem' }}
              >
                ➕ Add Option
              </button>
            </label>

            {formData.options.length === 0 && (
              <div className="adm-empty" style={{ marginTop: 8 }}>
                No options yet. Click "Add Option" to create one.
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 12 }}>
              {formData.options.map((option, optIdx) => (
                <div 
                  key={optIdx}
                  style={{
                    padding: 16,
                    background: '#f9fafb',
                    border: '2px solid #e5e7eb',
                    borderRadius: 12
                  }}
                >
                  {/* Option Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#111827' }}>
                      Option {optIdx + 1}
                    </div>
                    <button
                      type="button"
                      className="btn btn--danger"
                      onClick={() => removeOption(optIdx)}
                      style={{ fontSize: '0.8rem', padding: '4px 12px' }}
                    >
                      🗑️ Remove
                    </button>
                  </div>

                  {/* Option Text */}
                  <div className="form-group" style={{ marginBottom: 12 }}>
                    <input
                      type="text"
                      className="input"
                      value={option.text}
                      onChange={(e) => updateOption(optIdx, 'text', e.target.value)}
                      placeholder="Option text..."
                    />
                  </div>

                  {/* Phase 1 (Mindset) - Mindset Tag + Bucket */}
                  {formData.phase === 1 && (
                    <>
                      {/* Mindset Tag */}
                      <div className="form-group" style={{ marginBottom: 12 }}>
                        <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                          🧠 Mindset Tag
                        </label>
                        <select
                          className="input"
                          value={option.mindsetTag || ''}
                          onChange={(e) => updateOption(optIdx, 'mindsetTag', e.target.value)}
                        >
                          <option value="">None</option>
                          {availableMindsetTags.map(tag => (
                            <option key={tag.slug} value={tag.slug}>
                              {tag.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Bucket for Phase 1 */}
                      <div className="form-group">
                        <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                          🎯 Bucket Assignment (optional)
                        </label>
                        <select
                          className="input"
                          value={option.bucket || ''}
                          onChange={(e) => updateOption(optIdx, 'bucket', e.target.value)}
                        >
                          <option value="">None</option>
                          {BUCKET_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}

                  {/* Phase 2 (Bucket Discovery) - Bucket */}
                  {formData.phase === 2 && (
                    <div className="form-group">
                      <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                        🎯 Bucket Assignment
                      </label>
                      <select
                        className="input"
                        value={option.bucket || ''}
                        onChange={(e) => updateOption(optIdx, 'bucket', e.target.value)}
                      >
                        <option value="">Select bucket...</option>
                        {BUCKET_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 4 }}>
                        Choose which interest bucket this option represents
                      </div>
                    </div>
                  )}

                  {/* Phase 3 (Area Refinement) - Area Tags */}
                  {formData.phase === 3 && (
                    <div className="form-group">
                      <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                        📚 Area of Study Tags
                        {option.areaOfStudyTags && option.areaOfStudyTags.length > 0 && (
                          <span style={{ marginLeft: 8, color: '#0ea5e9' }}>
                            ({option.areaOfStudyTags.length} selected)
                          </span>
                        )}
                      </label>
                      <div style={{
                        maxHeight: 200,
                        overflowY: 'auto',
                        padding: 12,
                        background: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: 8
                      }}>
                        {loadingTags ? (
                          <div className="muted">Loading tags...</div>
                        ) : availableAreaTags.length === 0 ? (
                          <div className="adm-empty">No area tags available</div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {availableAreaTags.map(tag => {
                              const isSelected = (option.areaOfStudyTags || []).includes(tag.slug);
                              return (
                                <label 
                                  key={tag.slug}
                                  style={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    cursor: 'pointer',
                                    padding: '6px 8px',
                                    borderRadius: 6,
                                    background: isSelected ? '#eff6ff' : 'transparent',
                                    fontSize: '0.813rem'
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleOptionTag(optIdx, tag.slug, 'area')}
                                  />
                                  <span style={{ fontWeight: isSelected ? 600 : 400 }}>
                                    {tag.name}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Enabled */}
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.enabled}
                onChange={(e) => handleChange('enabled', e.target.checked)}
              />
              <span>Enabled (visible to quiz users)</span>
            </label>
          </div>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'Saving...' : isNew ? 'Create Question' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}